import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gift, Plus, Trash2, Calendar, ShoppingCart, Edit2, X, ChevronLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useApp } from '../context/AppContext';
import Navbar from './Navbar';

const Wishlist = () => {
  const { API, token, user, addToCart, t } = useApp();
  const navigate = useNavigate();
  const [wishlists, setWishlists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingWishlist, setEditingWishlist] = useState(null);
  const [products, setProducts] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    items: [],
    reminder_date: ''
  });

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    loadWishlists();
    loadProducts();
  }, [token, navigate]);

  const loadWishlists = async () => {
    try {
      const response = await axios.get(`${API}/wishlist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWishlists(response.data.wishlists || []);
    } catch (error) {
      console.error('Error loading wishlists:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?limit=100`);
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const handleCreateWishlist = async () => {
    if (!formData.name.trim()) {
      toast.error('Geef je wenslijst een naam');
      return;
    }
    if (formData.items.length === 0) {
      toast.error('Voeg minimaal 1 product toe');
      return;
    }

    try {
      if (editingWishlist) {
        await axios.put(`${API}/wishlist/${editingWishlist.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Wenslijst bijgewerkt');
      } else {
        await axios.post(`${API}/wishlist`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Wenslijst aangemaakt');
      }
      
      setShowCreateModal(false);
      setEditingWishlist(null);
      setFormData({ name: '', items: [], reminder_date: '' });
      loadWishlists();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Er ging iets mis');
    }
  };

  const handleDeleteWishlist = async (wishlistId) => {
    if (!window.confirm('Weet je zeker dat je deze wenslijst wilt verwijderen?')) return;
    
    try {
      await axios.delete(`${API}/wishlist/${wishlistId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Wenslijst verwijderd');
      loadWishlists();
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  const handleAddToCart = async (wishlist) => {
    for (const item of wishlist.items) {
      addToCart({
        product_id: item.product_id,
        name: item.name,
        brand: item.brand,
        image_url: item.image_url,
        size_ml: item.size_ml,
        price: item.price,
        quantity: item.quantity || 1
      });
    }
    toast.success(`${wishlist.items.length} items toegevoegd aan winkelwagen`);
    navigate('/cart');
  };

  const handleEditWishlist = (wishlist) => {
    setEditingWishlist(wishlist);
    setFormData({
      name: wishlist.name,
      items: wishlist.items.map(item => ({
        product_id: item.product_id,
        size_ml: item.size_ml,
        quantity: item.quantity || 1
      })),
      reminder_date: wishlist.reminder_date || ''
    });
    setShowCreateModal(true);
  };

  const addItemToForm = (productId) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;
    
    const defaultSize = product.sizes?.[0]?.ml || 50;
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { product_id: productId, size_ml: defaultSize, quantity: 1 }]
    }));
  };

  const removeItemFromForm = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateItemInForm = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, [field]: value } : item)
    }));
  };

  const calculateTotal = (items) => {
    return items.reduce((total, item) => total + (item.price || 0) * (item.quantity || 1), 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Navbar />
        <div className="flex justify-center items-center h-64">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/account')} className="p-2 hover:bg-white/5 rounded-lg">
              <ChevronLeft size={24} className="text-white" />
            </button>
            <div>
              <h1 className="text-2xl font-heading text-white flex items-center gap-2">
                <Gift className="text-[#D4AF37]" />
                {t('your_wishlist')}
              </h1>
              <p className="text-[#A8A29E] text-sm mt-1">{t('discover_products')}</p>
            </div>
          </div>
          
          <button
            onClick={() => { setEditingWishlist(null); setFormData({ name: '', items: [], reminder_date: '' }); setShowCreateModal(true); }}
            className="btn-primary flex items-center gap-2"
            data-testid="new-wishlist-btn"
          >
            <Plus size={20} />
            {t('new_address')}
          </button>
        </div>

        {/* Wishlists */}
        {wishlists.length === 0 ? (
          <div className="glass rounded-xl p-12 text-center" data-testid="wishlist-empty">
            <Gift size={48} className="mx-auto text-[#57534E] mb-4" />
            <h2 className="text-white text-xl mb-2">{t('wishlist_empty')}</h2>
            <p className="text-[#A8A29E] mb-6">{t('discover_products')}</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary"
              data-testid="create-wishlist-btn"
            >
              {t('new_address')}
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {wishlists.map((wishlist) => (
              <div key={wishlist.id} className="glass rounded-xl overflow-hidden">
                <div className="p-4 border-b border-[#2C1B18] flex justify-between items-start">
                  <div>
                    <h3 className="text-white font-medium text-lg">{wishlist.name}</h3>
                    <p className="text-[#57534E] text-sm">{wishlist.items.length} items</p>
                    {wishlist.reminder_date && (
                      <p className="text-[#D4AF37] text-sm flex items-center gap-1 mt-1">
                        <Calendar size={14} />
                        Herinnering: {new Date(wishlist.reminder_date).toLocaleDateString('nl-NL')}
                        {wishlist.reminder_sent && <span className="text-green-500 ml-2">✓ Verzonden</span>}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditWishlist(wishlist)}
                      className="p-2 hover:bg-white/10 rounded-lg text-[#A8A29E] hover:text-white transition-colors"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      onClick={() => handleDeleteWishlist(wishlist.id)}
                      className="p-2 hover:bg-red-500/10 rounded-lg text-[#A8A29E] hover:text-red-500 transition-colors"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
                
                {/* Items */}
                <div className="p-4 space-y-3 max-h-60 overflow-y-auto">
                  {wishlist.items.map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <img 
                        src={item.image_url} 
                        alt={item.name}
                        className="w-12 h-12 object-cover rounded-lg"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm truncate">{item.name}</p>
                        <p className="text-[#D4AF37] text-xs">{item.brand} • {item.size_ml}ml</p>
                      </div>
                      <div className="text-right">
                        <p className="text-white text-sm">€{(item.price * (item.quantity || 1)).toFixed(2)}</p>
                        <p className="text-[#57534E] text-xs">x{item.quantity || 1}</p>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Footer */}
                <div className="p-4 border-t border-[#2C1B18] flex justify-between items-center">
                  <div>
                    <span className="text-[#A8A29E] text-sm">Totaal:</span>
                    <span className="text-[#D4AF37] font-bold text-lg ml-2">€{calculateTotal(wishlist.items).toFixed(2)}</span>
                  </div>
                  <button
                    onClick={() => handleAddToCart(wishlist)}
                    className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] hover:bg-[#E5C048] text-black rounded-lg font-medium transition-colors"
                  >
                    <ShoppingCart size={18} />
                    Bestel Nu
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="glass rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-white text-xl font-heading">
                {editingWishlist ? 'Wenslijst Bewerken' : 'Nieuwe Wenslijst'}
              </h2>
              <button onClick={() => setShowCreateModal(false)} className="text-[#A8A29E] hover:text-white">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-6">
              {/* Name */}
              <div>
                <label className="block text-[#A8A29E] text-sm mb-2">Naam</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full bg-[#1C1917] border border-[#2C1B18] rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] outline-none"
                  placeholder="Bijv. Verjaardag Wishlist"
                />
              </div>

              {/* Reminder Date */}
              <div>
                <label className="block text-[#A8A29E] text-sm mb-2">
                  <Calendar size={16} className="inline mr-2" />
                  Herinnering Datum (optioneel)
                </label>
                <input
                  type="date"
                  value={formData.reminder_date?.split('T')[0] || ''}
                  onChange={(e) => setFormData({...formData, reminder_date: e.target.value})}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full bg-[#1C1917] border border-[#2C1B18] rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] outline-none"
                />
                <p className="text-[#57534E] text-xs mt-1">
                  Je ontvangt een e-mail herinnering op deze datum
                </p>
              </div>

              {/* Products */}
              <div>
                <label className="block text-[#A8A29E] text-sm mb-2">Producten</label>
                
                {/* Add Product */}
                <select
                  onChange={(e) => { if (e.target.value) { addItemToForm(e.target.value); e.target.value = ''; } }}
                  className="w-full bg-[#1C1917] border border-[#2C1B18] rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] outline-none mb-4"
                >
                  <option value="">+ Voeg product toe...</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.brand} - {product.name}
                    </option>
                  ))}
                </select>

                {/* Selected Items */}
                {formData.items.length > 0 ? (
                  <div className="space-y-3">
                    {formData.items.map((item, index) => {
                      const product = products.find(p => p.id === item.product_id);
                      if (!product) return null;
                      return (
                        <div key={index} className="bg-[#1C1917] rounded-lg p-3 flex items-center gap-3">
                          <img 
                            src={product.image_url} 
                            alt={product.name}
                            className="w-12 h-12 object-cover rounded-lg"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm truncate">{product.name}</p>
                            <p className="text-[#D4AF37] text-xs">{product.brand}</p>
                          </div>
                          <select
                            value={item.size_ml}
                            onChange={(e) => updateItemInForm(index, 'size_ml', parseInt(e.target.value))}
                            className="bg-[#2C1B18] border border-[#3C2B28] rounded px-2 py-1 text-white text-sm"
                          >
                            {product.sizes?.map((size) => (
                              <option key={size.ml} value={size.ml}>{size.ml}ml - €{size.price}</option>
                            ))}
                          </select>
                          <input
                            type="number"
                            min="1"
                            max="10"
                            value={item.quantity}
                            onChange={(e) => updateItemInForm(index, 'quantity', parseInt(e.target.value) || 1)}
                            className="w-16 bg-[#2C1B18] border border-[#3C2B28] rounded px-2 py-1 text-white text-sm text-center"
                          />
                          <button
                            onClick={() => removeItemFromForm(index)}
                            className="p-1 text-red-500 hover:bg-red-500/10 rounded"
                          >
                            <X size={18} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-[#57534E] text-center py-4">Nog geen producten toegevoegd</p>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 mt-6 pt-6 border-t border-[#2C1B18]">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 px-4 py-3 border border-[#2C1B18] rounded-lg text-[#A8A29E] hover:text-white transition-colors"
              >
                Annuleren
              </button>
              <button
                onClick={handleCreateWishlist}
                className="flex-1 btn-primary"
              >
                {editingWishlist ? 'Opslaan' : 'Wenslijst Maken'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Wishlist;
